<script>
	import { onMount } from "svelte";

	import { findLookupId } from "../../../helpers";
	import lookups from "../../../stores/lookupStore";
	import Person from "../../../models/Person";

	import TextInput from "../../../components/TextInput.svelte";
	import Checkbox from "../../../components/Checkbox.svelte";
	import Select from "../../../components/Select.svelte";
	import DatePicker from "../../../components/DatePicker.svelte";
	import FormRow from "../../../components/FormRow.svelte";
	import FormColumn from "../../../components/FormColumn.svelte";
	import AddButton from "../../../components/buttons/AddButton.svelte";

	export let claimant;
	export let relationshipToClaimant;
	export let executors;
	export let contactIsExecutor;
	export let contactFirstName = null;
	export let contactSurname = null;
	export let disabled = null;

	executors = executors || [];

	const addExecutor = () => {
		executors = [...executors, new Person()];
	};

	const deleteExecutor = (index) => {
		if (executors.length <= 1) return;

		if (executors[index].isContact) {
			contactIsExecutor = false;
		}

		executors.splice(index, 1);
		executors = [...executors];
	};

	const handleContactIsExecutorChange = (e) => {
		if (e.target.checked) {
			let contact = new Person();
			contact.firstName = contactFirstName;
			contact.surname = contactSurname;
			contact.isContact = true;

			executors.unshift(contact);

			let indexEmptyContact = executors.findIndex((e) => !e.firstName && !e.surname);
			if (indexEmptyContact >= 0) {
				deleteExecutor(indexEmptyContact);
			}
		} else {
			let indexContact = executors.findIndex((e) => e.isContact);
			if (indexContact >= 0) {
				if (executors[indexContact].isContact && executors.length === 1) {
					executors[indexContact].isContact = false;
					executors[indexContact].firstName = null;
					executors[indexContact].surname = null;
				} else {
					executors.splice(indexContact, 1);
				}
			}
		}

		executors = [...executors];
	};

	const handleContactNameChange = (contactFirstName, contactSurname) => {
		if (contactIsExecutor) {
			var indexContact = executors.findIndex((e) => e.isContact);
			if (indexContact >= 0) {
				executors[indexContact].firstName = contactFirstName;
				executors[indexContact].surname = contactSurname;
			}
		}

		executors = [...executors];
	};

	$: {
		handleContactNameChange(contactFirstName, contactSurname);
	}

	onMount(() => {
		if (!executors.length) {
			addExecutor();
		}

		var indexContact = executors.findIndex((e) => e.isContact);
		contactIsExecutor = indexContact >= 0;
	});

	const capacityOtherId = findLookupId($lookups.deceasedEstateCapacity, "Other");
</script>

<FormRow>
	<FormColumn cols="12">
		<TextInput
			required
			{disabled}
			label="Name of Deceased Person"
			bind:value={claimant.surname}
			id="claimantSurname"
			maxlength="50" />
	</FormColumn>
</FormRow>

<FormRow>
	<FormColumn cols="12">
		{@const relationshipToClaimantOptions = [
			{ label: "Parent", value: "Parent" },
			{ label: "Sibling", value: "Sibling" },
			{ label: "Spouse", value: "Spouse" },
			{ label: "Child", value: "Child" },
			{ label: "Other Family", value: "Other Family" },
			{ label: "Other", value: "Other" }
		]}

		<Select
			label="What is your relationship to the deceased?"
			required
			{disabled}
			bind:value={relationshipToClaimant}
			optionList={relationshipToClaimantOptions}
			textProperty="label"
			valueProperty="value"></Select>
	</FormColumn>
</FormRow>

<FormRow>
	<FormColumn cols="6">
		<DatePicker
			required
			{disabled}
			id="e_dateOfBirth"
			label="Date of death"
			validator={true}
			start={new Date(1924, 0, 1)}
			end={new Date()}
			placeholder="e.g. 01/05/1987"
			bind:formattedSelected={claimant.dateOfDeath}
			openOnClass="calendar-icon"
			on:dateChange
			on:dateSelected />
	</FormColumn>
</FormRow>

<FormRow>
	<FormColumn>
		<Checkbox
			{disabled}
			on:change={handleContactIsExecutorChange}
			bind:checked={contactIsExecutor}
			label="I am an executor"
			id="contactIsExecutor"></Checkbox>
	</FormColumn>
</FormRow>

<FormRow>
	<FormColumn>
		<Checkbox
			{disabled}
			required
			bind:checked={claimant.authorisedToAct}
			label="I am authorised to act on behalf of the Estate"
			id="authorisedToAct"></Checkbox>
	</FormColumn>
</FormRow>

<FormRow class="mt-0">
	<FormColumn cols="11">
		<FormRow>
			<FormColumn cols="6" class="d-none d-md-block">
				<!-- svelte-ignore a11y-label-has-associated-control -->
				<label>
					Executor first name
					<span class="required">*</span>
				</label>
			</FormColumn>
			<FormColumn cols="6" class="d-none d-md-block">
				<!-- svelte-ignore a11y-label-has-associated-control -->
				<label>
					Executor last name
					<span class="required">*</span>
				</label>
			</FormColumn>
		</FormRow>
	</FormColumn>
</FormRow>

<div class="executors">
	{#each executors as executor, i (executor)}
		<div class="executor">
			<FormRow class="mt-2">
				<FormColumn cols="11">
					<FormRow class="mt-0">
						<FormColumn cols="6">
							<TextInput
								required
								disabled={disabled || executor.isContact}
								placeholder="Executor first name"
								bind:value={executor.firstName}
								id="firstName{i}"
								maxlength="50" />
						</FormColumn>
						<FormColumn cols="6">
							<TextInput
								required
								disabled={disabled || executor.isContact}
								placeholder="Executor last  name"
								bind:value={executor.surname}
								id="surname{i}"
								maxlength="50" />
						</FormColumn>
					</FormRow>
				</FormColumn>
				<FormColumn cols="1" class="last mt-0">
					{#if executors.length > 1}
						<div class="confirmation-hide">
							<button
								on:click|preventDefault={() => deleteExecutor(i)}
								class="delete"
								title="Delete transaction"
								aria-label="Delete transaction">
								<i class="fas fa-trash"></i>
							</button>
						</div>
					{/if}
				</FormColumn>
			</FormRow>
		</div>
	{/each}

	<div class="confirmation-hide" style="margin-top: 20px;">
		<AddButton on:add={addExecutor}>Add another executor</AddButton>
	</div>
</div>

<FormRow>
	<FormColumn>
		<Select
			label="Capacity"
			required
			{disabled}
			id="cap"
			bind:value={claimant.capacityId}
			optionList={$lookups.deceasedEstateCapacity}
			textProperty="label"
			valueProperty="value"></Select>
	</FormColumn>
</FormRow>

{#if claimant.capacityId == capacityOtherId}
	<FormRow>
		<FormColumn>
			<TextInput
				{disabled}
				label="Please provide capacity details"
				bind:value={claimant.capacityOther}
				id="capacityOther"
				maxlength="250" />
		</FormColumn>
	</FormRow>
{/if}

<style>
	.executors {
		border-top: 1px solid var(--color-border);
	}

	.executor {
		position: relative;
	}

	.delete {
		border: none;
		outline: none;
		background-color: transparent;
		font-size: 1.1rem;
		font-weight: 700;
		color: var(--color-primary);
		margin-top: 10px;
	}
</style>
